import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import toast from 'react-hot-toast';
import { Session } from '../types';

const Profile: React.FC = () => {
  const { admin, logout } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await apiClient.getSessions();
      setSessions(res.data.data);
    } catch (e) {
      toast.error('Не удалось загрузить сессии');
    } finally {
      setLoadingSessions(false);
    }
  };

  React.useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsChanging(true);
    try {
      await apiClient.changePassword(oldPassword, newPassword);
      toast.success('Пароль успешно изменён');
      setOldPassword('');
      setNewPassword('');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Ошибка смены пароля');
    } finally {
      setIsChanging(false);
    }
  };

  const handleTerminateSession = async (id: string) => {
    try {
      await apiClient.terminateSession(id);
      toast.success('Сессия завершена');
      fetchSessions();
    } catch (e) {
      toast.error('Ошибка завершения сессии');
    }
  };

  const handleLogoutAll = async () => {
    try {
      await apiClient.logoutAllSessions();
      toast.success('Вы вышли со всех устройств (кроме текущего)');
      fetchSessions();
    } catch (e) {
      toast.error('Ошибка выхода со всех устройств');
    }
  };

  if (!admin) return null;

  return (
    <div className="max-w-2xl mx-auto py-8 space-y-8">
      <h1 className="text-2xl font-bold mb-4">Профиль</h1>
      <div className="bg-white rounded-lg shadow p-6 space-y-2">
        <div><b>Логин:</b> {admin.username}</div>
        <div><b>Email:</b> {admin.email || '-'}</div>
        <div><b>Роли:</b> {Array.isArray(admin.roles) && admin.roles.length > 0 ? admin.roles.map(r => r.name).join(', ') : '-'}</div>
      </div>

      <form onSubmit={handleChangePassword} className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold">Смена пароля</h2>
        <div>
          <label className="block text-sm font-medium mb-1">Старый пароль</label>
          <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full border rounded px-3 py-2" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Новый пароль</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full border rounded px-3 py-2" required minLength={6} />
        </div>
        <button type="submit" className="btn btn-primary" disabled={isChanging}>{isChanging ? 'Смена...' : 'Сменить пароль'}</button>
      </form>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Мои сессии</h2>
          <button onClick={handleLogoutAll} className="btn btn-danger">Выйти со всех устройств</button>
        </div>
        {loadingSessions ? (
          <div>Загрузка...</div>
        ) : (
          Array.isArray(sessions) && sessions.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Браузер</th>
                  <th>Устройство</th>
                  <th>Страна</th>
                  <th>Вход</th>
                  <th>Активность</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id} className={!s.is_valid ? 'text-gray-400 line-through' : ''}>
                    <td>{s.ip}</td>
                    <td>{s.browser || '-'}</td>
                    <td>{s.device || '-'}</td>
                    <td>{s.country || '-'}</td>
                    <td>{new Date(s.created_at).toLocaleString()}</td>
                    <td>{new Date(s.last_active).toLocaleString()}</td>
                    <td>
                      {s.is_valid && <button onClick={() => handleTerminateSession(s.id)} className="btn btn-xs btn-danger">Завершить</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>Нет сессий</div>
          )
        )}
      </div>

      <button onClick={logout} className="btn btn-secondary mt-4">Выйти из аккаунта</button>
    </div>
  );
};

export default Profile; 